import { useQuery } from '@tanstack/react-query'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { PermissionsHttpClient } from '@omnea-app/permissions/permissionsHttpClient'
import { recordGrantsQueryKey, type RecordAccessEntity } from './recordGrantsQuery'
import type { paths } from '@omnea-app/permissions/permissionsHttpClient/permissionsGeneratedTypes'

type RecordAccessResponse = paths['/entities/{entity}/{recordId}/access']['get']['responses'][200]['content']['application/json']

export type RecordAccessFor<Entity extends RecordAccessEntity> = Extract<RecordAccessResponse['access'], { entity: Entity }>
/** One entity's holders carry that entity's permissions; the default is every entity's, for code that serves them all. */
export type RecordAccessHolder<Entity extends RecordAccessEntity = RecordAccessEntity> =
  RecordAccessFor<Entity>['holders'][number]
export type RecordAccessGrantDetail = RecordAccessHolder['directGrant']

/** Who holds per-record access. One read fans out to a paginated authorization read per grant. */
export const useRecordGrants = (entity: RecordAccessEntity, recordId: string, { enabled = true }: { enabled?: boolean } = {}) => {
  const { getToken, user } = useAuthenticatedUser()
  return useQuery({
    queryKey: recordGrantsQueryKey(user.id, entity, recordId),
    queryFn: async () => {
      const authorization = await getToken()
      const permissionsClient = new PermissionsHttpClient({ authorization })
      const { data, error } = await permissionsClient.GET('/entities/{entity}/{recordId}/access', {
        params: { path: { entity, recordId } },
      })
      throwIfApiError(error)
      return data
    },
    // The dialog seeds its editable draft from this answer once, when it opens. Keeping nothing
    // cached between openings means that draft is always seeded from a read of its own, rather than
    // from a list another admin may have moved on from since the previous opening.
    staleTime: 0,
    gcTime: 0,
    enabled,
  })
}
